import React, { useState, useEffect, useCallback } from 'react';
import { roleAPI, companyAPI, applicationAPI, settingDefinitionsAPI } from '../services/api';
import logger from '../utils/logger';
import { PageLayout, Badge, SearchFilterBar, ConfirmDialog } from './ui';
import { Shield, Users, Lock, AlertTriangle, Save, Trash2, ChevronRight, ChevronDown, Settings } from 'lucide-react';

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [panelRole, setPanelRole] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [rolesResult, stResult] = await Promise.allSettled([
        roleAPI.getRoles(true),
        companyAPI.listAllServiceTypes()
      ]);
      if (rolesResult.status === 'fulfilled') setRoles(rolesResult.value.roles || []);
      else logger.error('Failed to load roles:', rolesResult.reason);
      if (stResult.status === 'fulfilled') setServiceTypes(stResult.value.service_types || []);
      else logger.error('Failed to load service types (BC API unavailable):', stResult.reason);
    } catch (err) {
      logger.error('Failed to load roles data:', err);
      setError('Failed to load roles data');
    } finally {
      setLoading(false);
    }
  };

  const openRolePanel = (role) => {
    setPanelRole(role);
  };

  const closePanel = () => {
    setPanelRole(null);
  };

  const handleRoleUpdated = async () => {
    await loadData();
    if (panelRole && panelRole.id) {
      const updated = roles.find(r => r.id === panelRole.id);
      if (updated) setPanelRole(updated);
    }
  };

  const handleDeleteRole = async (role) => {
    try {
      await roleAPI.deleteRole(role.id);
      setSuccess(`Role "${role.name}" deleted successfully`);
      setPanelRole(null);
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logger.error('Failed to delete role:', err);
      setError(err.response?.data?.error || 'Failed to delete role');
    } finally {
      setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
    }
  };

  const openCreatePanel = () => {
    setPanelRole({
      id: null,
      name: '',
      description: '',
      priority: 200,
      permissions: [],
      product: '',
    });
  };

  // Filter roles based on search term
  const filteredRoles = roles.filter(role => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      role.name.toLowerCase().includes(term) ||
      (role.description && role.description.toLowerCase().includes(term)) ||
      (role.product && role.product.toLowerCase().includes(term))
    );
  });

  return (
    <PageLayout
      title="Role Management"
      backTo="/management"
      backLabel="Back to Management"
      actions={
        <button onClick={openCreatePanel} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover text-sm font-medium">
          + Create Role
        </button>
      }
      subtitle="Define what each role can do in the IDP. Application access is configured from the Application page."
    >
      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4 text-sm text-green-700">{success}</div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-700">{error}</div>
      )}

      <SearchFilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search roles by name, description, or service type..."
      />

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading roles...</div>
      ) : filteredRoles.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No roles found</div>
      ) : (
        <div className="space-y-6">
          {(() => {
            // Group roles by service type
            const groups = {};
            filteredRoles.forEach(role => {
              const key = role.product || '';
              if (!groups[key]) groups[key] = [];
              groups[key].push(role);
            });
            // Sort: "Internal" (no product) first, then "Global", then alphabetical
            const sortedKeys = Object.keys(groups).sort((a, b) => {
              if (!a) return -1;
              if (!b) return 1;
              if (a === 'global') return -1;
              if (b === 'global') return 1;
              return a.localeCompare(b);
            });
            return sortedKeys.map(key => (
              <div key={key || '__general'} className="bg-white rounded-lg border">
                <div className="px-5 py-2.5 border-b bg-gray-50">
                  <h3 className="font-semibold text-gray-700 text-sm">
                    {!key ? 'Internal' : key === 'global' ? 'Global (All Companies)' : key.charAt(0).toUpperCase() + key.slice(1)}
                  </h3>
                </div>
                <div className="divide-y">
                  {groups[key].map(role => (
                    <div
                      key={role.id}
                      onClick={() => openRolePanel(role)}
                      className="px-5 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 text-sm truncate">{role.name}</span>
                          {role.effective_permissions?.includes('company_admin') && (
                            <span className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">Company Admin</span>
                          )}
                          {role.effective_permissions?.includes('webauthn_required') && (
                            <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded border border-red-200">Security Key Required</span>
                          )}
                          {role.effective_permissions?.includes('mfa_required') && !role.effective_permissions?.includes('webauthn_required') && (
                            <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded border border-red-200">MFA Required</span>
                          )}
                        </div>
                        {role.description && (
                          <div className="text-xs text-gray-500 mt-0.5 truncate">{role.description}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 ml-4 text-xs text-gray-400">
                        <span className="bg-gray-50 px-2 py-0.5 rounded-full">
                          {role.user_count != null ? `${role.user_count} users` : ''}
                        </span>
                        <span className="bg-gray-50 px-2 py-0.5 rounded-full">
                          {role.application_count || 0} apps
                        </span>
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {panelRole && (
        <RoleDetailPanel
          role={panelRole}
          serviceTypes={serviceTypes}
          onClose={closePanel}
          onUpdated={handleRoleUpdated}
          onDelete={(role) => setConfirmDialog({
            open: true,
            title: 'Delete Role',
            message: `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
            onConfirm: () => handleDeleteRole(role),
            variant: 'danger'
          })}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete Role"
        variant={confirmDialog.variant}
      />
    </PageLayout>
  );
};

// --- Role Detail Panel Component ---
const RoleDetailPanel = ({ role, serviceTypes, onClose, onUpdated, onDelete }) => {
  const isNew = !role.id;
  const [tab, setTab] = useState('General');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    name: role.name || '',
    description: role.description || '',
    priority: role.priority ?? 200,
    permissions: role.permissions || [],
    product: role.product || '',
  });

  // Users Assigned State
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Applications State
  const [allApps, setAllApps] = useState([]);
  const [assignedAppIds, setAssignedAppIds] = useState(new Set());
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsSaving, setAppsSaving] = useState(false);
  const [expandedAppId, setExpandedAppId] = useState(null);
  const [appSettings, setAppSettings] = useState({});       // { appId: [{ setting_key, setting_value }] }
  const [settingDefs, setSettingDefs] = useState({});        // { appId: [definitions] }
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [editedSettings, setEditedSettings] = useState({});  // { "appId:key": value }

  const tabs = isNew
    ? ['General']
    : ['General', 'Applications', 'Users', 'Advanced'];

  useEffect(() => {
    if (role.id) {
      setFormData({
        name: role.name || '',
        description: role.description || '',
        priority: role.priority ?? 200,
        permissions: role.permissions || [],
        product: role.product || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.id]);

  useEffect(() => {
    if (tab === 'Users' && role.id) {
      loadAssignedUsers();
    }
    if (tab === 'Applications' && role.id) {
      loadApplicationsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, role.id]);

  const loadAssignedUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await roleAPI.getRoleUsers(role.id);
      setAssignedUsers(res.users || []);
    } catch (err) {
      logger.error('Failed to load role users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadApplicationsData = useCallback(async () => {
    try {
      setAppsLoading(true);
      const [appsRes, roleAppsRes] = await Promise.all([
        applicationAPI.list(),
        roleAPI.getRoleApplications(role.id),
      ]);
      const apps = appsRes.applications || appsRes || [];
      setAllApps(Array.isArray(apps) ? apps : []);
      const assigned = (roleAppsRes.applications || []).map(a => a.id);
      setAssignedAppIds(new Set(assigned));
    } catch (err) {
      logger.error('Failed to load applications data:', err);
      setError('Failed to load applications');
    } finally {
      setAppsLoading(false);
    }
  }, [role.id]);

  const handleToggleApp = (appId) => {
    setAssignedAppIds(prev => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
        if (expandedAppId === appId) setExpandedAppId(null);
      } else {
        next.add(appId);
      }
      return next;
    });
  };

  const handleSaveApplications = async () => {
    try {
      setAppsSaving(true);
      setError('');
      await roleAPI.setRoleApplications(role.id, [...assignedAppIds]);
      setSuccess('Applications updated');
      await onUpdated();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save applications');
    } finally {
      setAppsSaving(false);
    }
  };

  const handleExpandApp = async (appId) => {
    if (expandedAppId === appId) {
      setExpandedAppId(null);
      return;
    }
    setExpandedAppId(appId);
    if (!settingDefs[appId] || !appSettings[appId]) {
      try {
        setSettingsLoading(true);
        const [defsRes, settingsRes] = await Promise.all([
          settingDefinitionsAPI.getDefinitions(appId),
          roleAPI.getRoleAppSettings(role.id, appId),
        ]);
        // Only show custom_field definitions in role settings (user_field/static_value are auto-resolved)
        const customFieldDefs = (defsRes.definitions || []).filter(d => !d.source_type || d.source_type === 'custom_field');
        setSettingDefs(prev => ({ ...prev, [appId]: customFieldDefs }));
        const currentSettings = settingsRes.settings || [];
        setAppSettings(prev => ({ ...prev, [appId]: currentSettings }));
        // Populate editedSettings with current values
        const edits = {};
        currentSettings.forEach(s => {
          edits[`${appId}:${s.setting_key}`] = s.setting_value;
        });
        setEditedSettings(prev => ({ ...prev, ...edits }));
      } catch (err) {
        logger.error('Failed to load app settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    }
  };

  const handleSettingChange = (appId, key, value) => {
    setEditedSettings(prev => ({ ...prev, [`${appId}:${key}`]: value }));
  };

  const handleSaveSettings = async (appId) => {
    try {
      setSettingsSaving(true);
      setError('');
      const defs = settingDefs[appId] || [];
      const settings = defs.map(d => ({
        setting_key: d.setting_key,
        setting_value: editedSettings[`${appId}:${d.setting_key}`] || '',
      })).filter(s => s.setting_value !== '');
      await roleAPI.setRoleAppSettings(role.id, appId, settings);
      // Refresh
      const settingsRes = await roleAPI.getRoleAppSettings(role.id, appId);
      setAppSettings(prev => ({ ...prev, [appId]: settingsRes.settings || [] }));
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveGeneral = async () => {
    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const payload = {
        ...formData,
        priority: parseInt(formData.priority) || 200
      };

      if (isNew) {
        // eslint-disable-next-line no-unused-vars
        const res = await roleAPI.createRole(payload);
        setSuccess('Role created successfully');
        await onUpdated();
        setTimeout(() => onClose(), 1500);
      } else {
        await roleAPI.updateRole(role.id, payload);
        setSuccess('Role details updated');
        await onUpdated();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save role details');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

  const renderSettingField = (def, appId) => {
    const key = `${appId}:${def.setting_key}`;
    const value = editedSettings[key] || '';

    if (def.field_type === 'select' && def.field_options) {
      const options = Array.isArray(def.field_options) ? def.field_options : [];
      return (
        <select value={value} onChange={e => handleSettingChange(appId, def.setting_key, e.target.value)}
          className={inputClass}>
          <option value="">— Select —</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label || opt.value}</option>
          ))}
        </select>
      );
    }
    if (def.field_type === 'boolean') {
      return (
        <select value={value} onChange={e => handleSettingChange(appId, def.setting_key, e.target.value)}
          className={inputClass}>
          <option value="">— Not set —</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }
    if (def.field_type === 'textarea') {
      return (
        <textarea value={value} onChange={e => handleSettingChange(appId, def.setting_key, e.target.value)}
          rows="2" className={inputClass} />
      );
    }
    return (
      <input type={def.field_type === 'number' ? 'number' : 'text'} value={value}
        onChange={e => handleSettingChange(appId, def.setting_key, e.target.value)}
        className={inputClass} />
    );
  };

  return (
    <div className="fixed right-0 bottom-0 bg-white border-l border-gray-200 shadow-2xl z-40 overflow-y-auto" 
         style={{ top: '64px', width: '900px', maxWidth: '90vw' }}>
      
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center gap-3 z-10">
        <Shield className="w-6 h-6 text-primary" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{role.name || 'New Role'}</div>
          {role.product && <div className="text-sm text-gray-500 truncate">{role.product}</div>}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2">&times;</button>
      </div>

      {/* Tabs */}
      {!isNew && (
        <div className="sticky top-[73px] bg-white border-b border-gray-200 flex overflow-x-auto z-10">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Content Area */}
      <div className="px-6 pt-7 pb-10">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700 mb-4">{error}</div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-3 text-sm text-green-700 mb-4">{success}</div>
        )}

        {/* General Tab */}
        {tab === 'General' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1 font-semibold">Role Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g. Veeam Administrator"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1 font-semibold">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="2"
                placeholder="Brief description of what this role manages"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1 font-semibold">Service Type</label>
              <select
                name="product"
                value={formData.product}
                onChange={handleInputChange}
                className={inputClass}
              >
                <option value="">— None (internal only) —</option>
                <option value="global">global (all companies)</option>
                {serviceTypes.filter(st => st !== 'global').map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">None = internal companies only. Global = all companies. Service type = only companies with that service.</p>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1 font-semibold">Priority (Lower number = higher priority)</label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                When a user has multiple roles assigned, the one with the lowest priority number determines application settings values.
              </p>
            </div>

            {/* Permission Toggles */}
            <div className="space-y-2.5 pt-2">
              <div className="flex items-center gap-3 p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl">
                <input
                  type="checkbox"
                  id="perm_company_admin"
                  checked={formData.permissions.includes('company_admin')}
                  onChange={(e) => {
                    const perm = 'company_admin';
                    setFormData(prev => ({
                      ...prev,
                      permissions: e.target.checked
                        ? [...prev.permissions, perm]
                        : prev.permissions.filter(p => p !== perm)
                    }));
                  }}
                  className="rounded text-primary focus:ring-primary h-4 w-4"
                />
                <label htmlFor="perm_company_admin" className="cursor-pointer">
                  <div className="text-xs font-semibold text-amber-950 flex items-center gap-1">
                    <Users className="w-4 h-4 text-amber-700" />
                    Company Admin
                  </div>
                  <p className="text-xs text-amber-800/80 mt-0.5 leading-normal">
                    Assigned users can invite, remove, and manage other users in their company.
                  </p>
                </label>
              </div>

              <div className="flex items-center gap-3 p-3 bg-red-50/70 border border-red-200/80 rounded-xl">
                <input
                  type="checkbox"
                  id="perm_mfa_required"
                  checked={formData.permissions.includes('mfa_required')}
                  onChange={(e) => {
                    const perm = 'mfa_required';
                    setFormData(prev => ({
                      ...prev,
                      permissions: e.target.checked
                        ? [...prev.permissions, perm]
                        : prev.permissions.filter(p => p !== perm)
                    }));
                  }}
                  className="rounded text-primary focus:ring-primary h-4 w-4"
                />
                <label htmlFor="perm_mfa_required" className="cursor-pointer">
                  <div className="text-xs font-semibold text-red-950 flex items-center gap-1">
                    <Lock className="w-4 h-4 text-red-700" />
                    Require MFA
                  </div>
                  <p className="text-xs text-red-800/80 mt-0.5 leading-normal">
                    Enforces two-factor authentication (TOTP or Security Key) before allowing login.
                  </p>
                </label>
              </div>

              <div className="flex items-center gap-3 p-3 bg-red-50/70 border border-red-200/80 rounded-xl">
                <input
                  type="checkbox"
                  id="perm_webauthn_required"
                  checked={formData.permissions.includes('webauthn_required')}
                  onChange={(e) => {
                    const perm = 'webauthn_required';
                    setFormData(prev => ({
                      ...prev,
                      permissions: e.target.checked
                        ? [...prev.permissions, perm]
                        : prev.permissions.filter(p => p !== perm)
                    }));
                  }}
                  className="rounded text-primary focus:ring-primary h-4 w-4"
                />
                <label htmlFor="perm_webauthn_required" className="cursor-pointer">
                  <div className="text-xs font-semibold text-red-950 flex items-center gap-1">
                    <Lock className="w-4 h-4 text-red-700" />
                    Require Security Key
                  </div>
                  <p className="text-xs text-red-800/80 mt-0.5 leading-normal">
                    Requires a registered security key (YubiKey / WebAuthn) — TOTP alone is not sufficient.
                  </p>
                </label>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <button
                onClick={handleSaveGeneral}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
              >
                <Save className="w-4 h-4" />
                {isNew ? 'Create Role' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {tab === 'Applications' && !isNew && (
          <div className="space-y-4">
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-xl p-3">
              Select which applications this role grants access to. Expand an assigned application to configure its default settings for this role.
            </div>

            {appsLoading ? (
              <div className="text-center py-6 text-xs text-gray-500">Loading applications...</div>
            ) : allApps.length === 0 ? (
              <div className="text-center py-8 text-xs text-gray-500 bg-gray-50 rounded-xl border border-dashed">
                No applications configured yet.
              </div>
            ) : (
              <>
                <div className="border rounded-lg divide-y">
                  {allApps.map(app => {
                    const isAssigned = assignedAppIds.has(app.id);
                    const isExpanded = expandedAppId === app.id && isAssigned;
                    const defs = settingDefs[app.id] || [];

                    return (
                      <div key={app.id}>
                        <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            onChange={() => handleToggleApp(app.id)}
                            className="rounded text-primary focus:ring-primary h-4 w-4 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => isAssigned && handleExpandApp(app.id)}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate">{app.label || app.name}</span>
                              {app.application_type && (
                                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{app.application_type}</span>
                              )}
                            </div>
                          </div>
                          {isAssigned && (
                            <button onClick={() => handleExpandApp(app.id)}
                              className="text-gray-400 hover:text-gray-600 p-1">
                              {isExpanded
                                ? <ChevronDown className="w-4 h-4" />
                                : <Settings className="w-4 h-4" />
                              }
                            </button>
                          )}
                        </div>

                        {/* Expanded settings area */}
                        {isExpanded && (
                          <div className="bg-gray-50 border-t px-6 py-4">
                            {settingsLoading ? (
                              <div className="text-xs text-gray-500 py-2">Loading settings...</div>
                            ) : defs.length === 0 ? (
                              <div className="text-xs text-gray-500 py-2">No configurable settings for this application.</div>
                            ) : (
                              <div className="space-y-3">
                                <div className="text-xs font-semibold text-gray-600 mb-2">Default Settings for This Role</div>
                                {defs.map(def => (
                                  <div key={def.setting_key}>
                                    <label className="block text-xs text-gray-500 mb-1 font-medium">
                                      {def.setting_label || def.setting_key}
                                    </label>
                                    {renderSettingField(def, app.id)}
                                  </div>
                                ))}
                                <div className="flex justify-end pt-2">
                                  <button
                                    onClick={() => handleSaveSettings(app.id)}
                                    disabled={settingsSaving}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-xs font-medium"
                                  >
                                    <Save className="w-3.5 h-3.5" />
                                    Save Settings
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveApplications}
                    disabled={appsSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
                  >
                    <Save className="w-4 h-4" />
                    {appsSaving ? 'Saving...' : 'Save Applications'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'Users' && !isNew && (
          <div className="space-y-4">
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-xl p-3">
              This lists all user accounts currently assigned to this role. Assignments are managed per company via <strong>Admin Panel &rarr; Users</strong>.
            </div>

            {usersLoading ? (
              <div className="text-center py-6 text-xs text-gray-500">Loading assigned users...</div>
            ) : assignedUsers.length === 0 ? (
              <div className="text-center py-8 text-xs text-gray-500 bg-gray-50 rounded-xl border border-dashed">
                No users currently assigned to this role.
              </div>
            ) : (
              <div className="space-y-2 max-h-[380px] overflow-y-auto">
                {assignedUsers.map(user => (
                  <div key={user.id} className="p-3 border border-slate-100 bg-white rounded-xl flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold text-slate-800">{user.firstname} {user.lastname}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{user.email}</div>
                    </div>
                    {user.company_name && (
                      <Badge color="gray">{user.company_name}</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Advanced Tab */}
        {tab === 'Advanced' && !isNew && (
          <div className="space-y-4 animate-fade">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 text-sm">Danger Zone</h3>
                  <p className="text-sm text-red-700 mt-1">Actions in this section are irreversible and may disrupt user access.</p>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">Delete Role</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Permanently delete this role configuration. All user company scope role assignments will be revoked automatically.
                  </p>
                </div>
                <button
                  onClick={() => onDelete(role)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManager;
